import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name"),
  email: z.string().trim().email("Enter a valid email"),
  company: z.string().trim().optional(),
  message: z.string().trim().min(10, "Tell us a little about what you need (10+ characters)"),
});

export default function ContactForm() {
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (values) => {
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, values);
      setSent(true);
      reset();
    } catch (e) {
      toast.error("Couldn't send your message — please email hello@postyourcontent.com directly.");
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-3xl border border-emerald-200 bg-emerald-50 p-10 text-center"
        data-testid="contact-success-message"
      >
        <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={36} />
        <h3 className="text-lg font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>
          Thanks — we've got it!
        </h3>
        <p className="text-sm text-slate-600">We'll be in touch shortly to set up your demo.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="contact-form" noValidate>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1.5">Name</label>
        <Input
          {...register("name")}
          placeholder="Jane Smith"
          className="rounded-xl h-11"
          data-testid="contact-name-input"
        />
        {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="contact-name-error">{errors.name.message}</p>}
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1.5">Email</label>
        <Input
          {...register("email")}
          type="email"
          placeholder="jane@company.com"
          className="rounded-xl h-11"
          data-testid="contact-email-input"
        />
        {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="contact-email-error">{errors.email.message}</p>}
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1.5">Company (optional)</label>
        <Input
          {...register("company")}
          placeholder="Acme Inc."
          className="rounded-xl h-11"
          data-testid="contact-company-input"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1.5">What are you looking to do?</label>
        <Textarea
          {...register("message")}
          rows={4}
          placeholder="Tell us about your team and what you'd like PYC to handle..."
          className="rounded-xl"
          data-testid="contact-message-textarea"
        />
        {errors.message && <p className="text-xs text-red-600 mt-1" data-testid="contact-message-error">{errors.message.message}</p>}
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-7 py-3.5 text-sm font-bold tracking-tight hover:bg-slate-700 transition-colors disabled:opacity-60"
        style={{ fontFamily: "Outfit" }}
      >
        {isSubmitting && <Loader2 size={16} className="animate-spin" />}
        {isSubmitting ? "Sending…" : "Book a Demo"}
      </button>
    </form>
  );
}
